import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ListView } from 'src/components/templates';
import { selectors as authSelectors } from 'src/features/auth/authSlice';
import { DOCUMENT_TYPES } from 'src/constants/documentTypes';
import { getPublicLink } from 'src/helpers/getDUPLink';
import usePropertiesDDown from 'src/features/property/hooks/usePropertiesDDown';
import { configVars } from 'src/helpers';
import theme from 'src/theme';
import { useUserFeature } from 'src/hooks';
import * as S from './styles';

type DashboardTab = 'review' | 'signals';

interface ManualDocument {
  type: string;
  ruled_by_av: boolean;
  av_result?: string;
}

interface TestScenarioParams {
  property_short_id: string;
  workflow: string;
  manual_documents: ManualDocument[];
  potential_matches: number;
  reviewer_id?: string;
}

// Signal scenario types
interface SignalScenarioResult {
  signal_type: string;
  condition: string;
  entries: Array<{
    entry_id: string;
    folder_id: string;
    folder_name: string;
    applicant_id: string;
  }>;
  applicant_identity: {
    email: string;
    phone: string;
    first_name: string;
    last_name: string;
  };
  properties: Array<{ short_id: string; company_short_id: string; city: string }>;
  trigger_property: { short_id: string; company_short_id: string; city: string };
  cities?: string[];
  message: string;
}

type SignalCondition = 'high_volume' | 'patterned_fraud';

// ITL-3002 — seeded FVS fixture variants served by the backend SimulatedClient
// (must match Snappt.FinancialVerifications.SimulatedFixtures.variants/0).
const VOA_FIXTURES = [
  { id: 'extracted_single', label: 'Extracted — single account', documents: 1 },
  { id: 'audit_only', label: 'Audit-only — no extractable data', documents: 1 },
  { id: 'multi_account_multi_statement', label: 'Multi-account, "Document 1 of 2"', documents: 2 },
  { id: 'partial_extraction', label: 'Partial extraction — null fields', documents: 1 },
  { id: 'status_error', label: 'Status error — warning codes', documents: 1 },
  { id: 'empty_extraction', label: 'Empty extraction — no accounts, no documents', documents: 2 }
] as const;
type VoaFixtureId = (typeof VOA_FIXTURES)[number]['id'];

// Proof type for the seeded documents. Paystubs are not asset proofs, so seeding
// with them yields an entry the VOA review page shows as having nothing to review.
const VOA_PROOF_TYPES = [
  { id: 'bank_statement', label: 'Bank statement (asset proof)' },
  { id: 'paystub', label: 'Paystub (not an asset proof)' }
] as const;
type VoaProofType = (typeof VOA_PROOF_TYPES)[number]['id'];

const VOA_CLAIM_RESPONSES = [
  { id: 'ok', label: 'Claim 200 — granted' },
  { id: 'conflict', label: 'Claim 409 — conflict (archives the item)' },
  { id: 'already_resolved', label: 'Claim 400 — already resolved (archives the item)' }
] as const;
type VoaClaimResponse = (typeof VOA_CLAIM_RESPONSES)[number]['id'];

const VOA_COMPLETE_RESPONSES = [
  { id: 'ok', label: 'Complete 200 — accepted' },
  { id: 'conflict', label: 'Complete 409 — conflict' },
  { id: 'timeout', label: 'Complete — timeout' }
] as const;
type VoaCompleteResponse = (typeof VOA_COMPLETE_RESPONSES)[number]['id'];

interface VoaSeededScenario {
  entryId: string;
  applicantId: string;
  // review_items PK is an integer, unlike the uuid ids around it
  reviewItemId: number | null;
  fixture: string;
}

// FVS-backed screenshot staging (deployed dev, where the simulated client is
// not wired): candidates are applicants FVS already has records for.
interface VoaScreenshotCandidate {
  applicant_id: string;
  entry_id: string;
  applicant_name: string | null;
  account_count: number;
  document_count: number;
  review_status: string | null;
}

const isZeroExtraction = (candidate: VoaScreenshotCandidate): boolean =>
  candidate.document_count === 0 && candidate.account_count === 0;

const voaCandidateLabel = (candidate: VoaScreenshotCandidate): string => {
  const kind = isZeroExtraction(candidate)
    ? 'Zero extraction'
    : candidate.document_count > 1
    ? 'Multi-document'
    : candidate.account_count > 1
    ? 'Multi-account'
    : 'Simple';
  const name = candidate.applicant_name ?? candidate.applicant_id.slice(0, 8);
  return (
    `${kind} — ${name} ` +
    `(${candidate.document_count} doc${candidate.document_count === 1 ? '' : 's'}, ` +
    `${candidate.account_count} account${candidate.account_count === 1 ? '' : 's'})`
  );
};

// ITL-3253 simulated Gamma Ray. Every one of these comes from
// GET /dev_dashboard/document_evaluation_checks, so the catalog and the
// presets have exactly one definition and it lives in Elixir.
interface GammaRayOutcome {
  value: string;
  label: string;
  pass: boolean;
}

interface GammaRayCheck {
  engine_key: string;
  display_name: string;
  outcomes: GammaRayOutcome[];
  match_bar: number;
  floor: number;
}

interface GammaRayCandidate {
  score: number;
  notes?: unknown[];
  exception?: unknown;
}

// A row is the scores the simulator should report for one check, or the
// reason it did not run. Everything else on a candidate is fabricated in Elixir,
// except the notes and exception a preset carries through unchanged.
interface GammaRayRow {
  engine_key: string;
  candidates: GammaRayCandidate[];
  not_run_reason: string | null;
  template?: string | null;
  variant?: string | null;
}

interface GammaRayTrendCheck {
  engine_key: string;
  display_name: string;
  templates: string[];
}

type GammaRayResult = 'cleared' | 'match' | 'not_certain' | 'pick_one';

// The editor holds the text in each score box rather than a number. A controlled
// number input is rewritten from state after every change, so holding a number
// snaps an emptied box back to its old score before the tester can type the new
// one. Number() runs on the band and on publish instead.
interface GammaRayCandidateDraft {
  score: string;
  notes?: unknown[];
  exception?: unknown;
}

interface GammaRayRowDraft {
  engine_key: string;
  candidates: GammaRayCandidateDraft[];
  not_run_reason: string | null;
  template?: string | null;
  variant?: string | null;
}

interface GammaRayScenario {
  id: string;
  label: string;
  rows: GammaRayRow[];
}

interface GammaRayProof {
  proof_id: string;
  type: string;
}

interface GammaRayEntry {
  entry_id: string;
  folder_id: string;
  applicant_name: string | null;
  proof_count: number;
  proofs: GammaRayProof[];
}

interface GammaRayCatalog {
  checks: GammaRayCheck[];
  fraud_trend_checks?: GammaRayTrendCheck[];
  trend_variants?: string[];
  scenarios: GammaRayScenario[];
  max_candidates: number;
  not_run_reasons: string[];
}

const gammaRayDraftRows = (rows: GammaRayRow[]): GammaRayRowDraft[] =>
  rows.map((row) => ({
    ...row,
    candidates: row.candidates.map((candidate) => ({
      ...candidate,
      score: String(Number(candidate.score.toFixed(4)))
    }))
  }));

const GAMMA_RAY_REGISTRY_KEY = 'employer_payroll_registry';

// The simulator names candidates by rank, best score first, except on the
// registry, which keeps the order they are typed in. Mirrors that so each box
// says which candidate it becomes in the review.
const gammaRayCandidatePosition = (row: GammaRayRowDraft, index: number): number => {
  if (row.engine_key === GAMMA_RAY_REGISTRY_KEY) return index + 1;

  const score = gammaRayScore(row.candidates[index]);
  return (
    1 +
    row.candidates.filter((other, i) => {
      const otherScore = gammaRayScore(other);
      return otherScore > score || (otherScore === score && i < index);
    }).length
  );
};

const gammaRayCandidateEffect = (check: GammaRayCheck, score: number): string => {
  if (!Number.isFinite(score)) return '';
  if (score >= check.match_bar) return `at or above the ${check.match_bar} bar, counts as a match`;
  if (score >= check.floor) return 'close, the analyst decides';
  return `below the ${check.floor} floor, shown but ignored`;
};

// The simulator puts candidate n on sheet row n + 1, under the header row.
const gammaRayCandidateLabel = (
  check: GammaRayCheck,
  row: GammaRayRowDraft,
  index: number
): string => {
  const position = gammaRayCandidatePosition(row, index);
  const effect = gammaRayCandidateEffect(check, gammaRayScore(row.candidates[index]));
  const name = `Candidate ${position} (Row ${position + 1})`;
  return effect ? `${name}: ${effect}` : name;
};

// NaN for an empty or unparseable box, so it neither counts as a score nor
// publishes as one.
const gammaRayScore = (candidate: GammaRayCandidateDraft): number =>
  candidate.score.trim() === '' ? NaN : Number(candidate.score);

const gammaRayWireRows = (rows: GammaRayRowDraft[]): GammaRayRow[] =>
  rows.map((row) => ({
    ...row,
    candidates: row.candidates.map((candidate) => ({
      ...candidate,
      score: gammaRayScore(candidate)
    }))
  }));

const gammaRayScoresComplete = (rows: GammaRayRowDraft[]): boolean =>
  rows.every((row) =>
    row.candidates.every((candidate) => Number.isFinite(gammaRayScore(candidate)))
  );

// What the platform will read off the top scores once ITL-3255 derives it,
// shown so a tester knows what a score edit means before publishing.
const gammaRayBand = (check: GammaRayCheck, row: GammaRayRowDraft): string | null => {
  if (row.not_run_reason !== null) return null;

  const scores = row.candidates.map(gammaRayScore).filter(Number.isFinite);
  const aboveBar = scores.filter((score) => score >= check.match_bar).length;

  if (aboveBar > 1) return 'Pick one';
  if (aboveBar === 1) return 'Match';
  if (scores.some((score) => score >= check.floor)) return 'Not Certain';
  return 'Cleared';
};

const GAMMA_RAY_RESULTS: { value: GammaRayResult; band: string; label: string }[] = [
  { value: 'cleared', band: 'Cleared', label: 'Cleared, no list row close enough' },
  { value: 'match', band: 'Match', label: 'Match, one list row above the bar' },
  { value: 'not_certain', band: 'Not Certain', label: 'Not certain, close but under the bar' },
  { value: 'pick_one', band: 'Pick one', label: 'Pick one, two list rows above the bar' }
];

const GAMMA_RAY_VARIANT_LABELS: Record<string, string> = {
  consistent: 'Looks normal',
  blocked: 'Potential edit',
  abstain: "Couldn't tell",
  partial: 'Header bars: separator fired, bars could not tell (Bank of America)',
  packing: 'Embedded fonts: mixed packing only (Bank of America)',
  uncataloged: 'Potential edit plus a pattern the catalog does not list (ADP)',
  no_visual: 'Potential edit, no page view file',
  not_applicable: 'No template matched',
  silent: 'No answer, the engine never replies'
};

const GAMMA_RAY_NOT_RUN = 'not_run:';

const gammaRayResultOf = (band: string | null): GammaRayResult =>
  GAMMA_RAY_RESULTS.find((result) => result.band === band)?.value ?? 'cleared';

// Scores that read as the picked result, matching the presets' bands.
const gammaRayResultScores = (check: GammaRayCheck, result: GammaRayResult): string[] => {
  const round = (score: number) => String(Number(score.toFixed(2)));
  const above = round(Math.min(check.match_bar + 0.06, 1));

  if (result === 'match') return [above];
  if (result === 'pick_one') return [above, above];
  if (result === 'not_certain') return [round((check.match_bar + check.floor) / 2)];
  return [round(Math.max(check.floor - 0.1, 0))];
};

interface GammaRayRequested {
  entry_id: string;
  requested: { proof_id: string; evaluation_id: string }[];
}

const gammaRayEntryLabel = (entry: GammaRayEntry | null): string => {
  if (!entry) return 'No entry yet';

  const name = entry.applicant_name || entry.entry_id.slice(0, 8);
  const docs = entry.proof_count === 1 ? '1 document' : `${entry.proof_count} documents`;

  return `${name} (${docs})`;
};

const VOA_FLOWS = [
  {
    id: 'direct',
    label: 'Screen review — seed assigned to me (no event, no engine)'
  },
  {
    id: 'event',
    label: 'Event flow — publish needs_review → consumer → engine claim'
  }
] as const;
type VoaFlowId = (typeof VOA_FLOWS)[number]['id'];

// Navigating to the VOA screen remounts this page, so the seeded-scenario
// handle must survive outside component state or Publish/Reset lock out.
// Known gap: no `storage` event listener, so two open tabs can diverge after
// a reset until the stale tab's next action 404s (which clears its handle).
const VOA_SCENARIO_STORAGE_KEY = 'devDashboard.voaScenario';

const readStoredVoaScenario = (): VoaSeededScenario | null => {
  try {
    const raw = window.localStorage.getItem(VOA_SCENARIO_STORAGE_KEY);
    if (!raw) return null;

    // The stored shape changes across releases, so an older-shape or hand-edited
    // blob must fail cleanly here, not as an opaque 404 at request time.
    const parsed = JSON.parse(raw) as VoaSeededScenario;
    if (typeof parsed?.applicantId === 'string' && typeof parsed?.entryId === 'string') {
      return parsed;
    }

    window.localStorage.removeItem(VOA_SCENARIO_STORAGE_KEY);
    return null;
  } catch {
    return null;
  }
};

const storeVoaScenario = (scenario: VoaSeededScenario | null) => {
  try {
    if (scenario) {
      window.localStorage.setItem(VOA_SCENARIO_STORAGE_KEY, JSON.stringify(scenario));
    } else {
      window.localStorage.removeItem(VOA_SCENARIO_STORAGE_KEY);
    }
  } catch {
    // storage unavailable - the handle degrades to session-only state
  }
};

const DevDashboardPage: React.FC = () => {
  const user = useSelector(authSelectors.user);
  const navigate = useNavigate();
  const hasDevDashboardAccess = useUserFeature('dev_dashboard');
  const { onGetPropertiesDDown, getPropertiesDDown: propertiesDDown } = usePropertiesDDown();

  const {
    data: propertyData,
    isIdle: propertyIsIdle,
    isLoading: isLoadingProperties,
    count: propertyCount
  } = propertiesDDown;

  const [isLoading, setIsLoading] = useState(false);
  const [filteredProperties, setFilteredProperties] = useState<any[]>([]);
  const [propertySearchTerm, setPropertySearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [redirectToReview, setRedirectToReview] = useState(true);

  const [formData, setFormData] = useState<TestScenarioParams>({
    property_short_id: '',
    workflow: 'fraud_detection',
    manual_documents: [{ type: 'pay_stub', ruled_by_av: false }],
    potential_matches: 0,
    reviewer_id: ''
  });

  // Signal scenario state
  const [signalCondition, setSignalCondition] = useState<SignalCondition>('high_volume');
  const [signalLoading, setSignalLoading] = useState(false);
  const [signalResult, setSignalResult] = useState<SignalScenarioResult | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);

  // FVS-backed screenshot staging state
  const [voaCandidates, setVoaCandidates] = useState<VoaScreenshotCandidate[]>([]);
  const [voaCandidatesLoading, setVoaCandidatesLoading] = useState(false);
  const [selectedVoaCandidate, setSelectedVoaCandidate] = useState('');

  // ITL-3002 — Simulate FVS section (collapsed by default; simulation only
  // works in local dev, so most dashboard visits don't need it)
  const [simulateFvsOpen, setSimulateFvsOpen] = useState(false);
  const [voaFixture, setVoaFixture] = useState<VoaFixtureId>('extracted_single');
  const [voaProofType, setVoaProofType] = useState<VoaProofType>('bank_statement');
  const [voaClaimResponse, setVoaClaimResponse] = useState<VoaClaimResponse>('ok');
  const [voaCompleteResponse, setVoaCompleteResponse] = useState<VoaCompleteResponse>('ok');
  const [voaScenario, setVoaScenarioState] = useState<VoaSeededScenario | null>(
    readStoredVoaScenario
  );

  // An event-path seed (no review item) restores into event mode so Publish
  // stays reachable after the remount. Derived from the state initialized on
  // the line above — useState initializers run in order on first render.
  const [voaFlow, setVoaFlow] = useState<VoaFlowId>(() =>
    voaScenario && voaScenario.reviewItemId === null ? 'event' : 'direct'
  );

  const setVoaScenario = (scenario: VoaSeededScenario | null) => {
    setVoaScenarioState(scenario);
    storeVoaScenario(scenario);
  };

  // Tab state
  // ITL-3253. The rows travel in the POST body of the same request that
  // publishes them, so nothing persists them: no localStorage, no backend
  // state, nothing to reset.
  const [gammaRayOpen, setGammaRayOpen] = useState(false);
  const [gammaRayCatalog, setGammaRayCatalog] = useState<GammaRayCatalog | null>(null);
  const [gammaRayScenarioId, setGammaRayScenarioId] = useState('varied_default');
  const [gammaRayRows, setGammaRayRows] = useState<GammaRayRowDraft[]>([]);
  const [gammaRayEntry, setGammaRayEntry] = useState<GammaRayEntry | null>(null);
  const [gammaRayProofIds, setGammaRayProofIds] = useState<string[]>([]);
  const [gammaRayShowScores, setGammaRayShowScores] = useState(false);

  const [activeTab, setActiveTab] = useState<DashboardTab>('review');

  const documentTypes = [...DOCUMENT_TYPES.map((docType) => docType.key), 'w2'];

  const avResults = ['CLEAN', 'EDITED', 'UNDETERMINED'];

  const handleInputChange = (field: keyof TestScenarioParams, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePropertySearch = (searchTerm: string) => {
    setPropertySearchTerm(searchTerm);
    setHighlightedIndex(-1);

    if (!searchTerm.trim()) {
      setFilteredProperties(propertyData || []);
    } else {
      const filtered = (propertyData || []).filter(
        (property) =>
          property.short_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          property.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProperties(filtered);
    }
  };

  const handlePropertySelect = (property: { id: string; short_id: string; name: string }) => {
    setPropertySearchTerm(`${property.short_id} - ${property.name}`);
    handleInputChange('property_short_id', property.short_id);
    setIsDropdownOpen(false);
    setHighlightedIndex(-1);
  };

  const handleDropdownToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDropdownOpen(!isDropdownOpen);
    if (!isDropdownOpen) {
      setHighlightedIndex(-1);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isDropdownOpen || filteredProperties.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < filteredProperties.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filteredProperties.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handlePropertySelect(filteredProperties[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleManualDocumentChange = (index: number, field: keyof ManualDocument, value: any) => {
    setFormData((prev) => {
      const updatedDocs = prev.manual_documents.map((doc, i) => {
        if (i === index) {
          const updatedDoc = { ...doc, [field]: value };
          if (field === 'ruled_by_av' && !value) {
            updatedDoc.av_result = undefined;
          }
          return updatedDoc;
        }
        return doc;
      });

      if (field === 'ruled_by_av' && value === true) {
        const nonAVDocs = updatedDocs.filter((doc) => !doc.ruled_by_av);
        if (nonAVDocs.length === 0) {
          updatedDocs.push({ type: 'pay_stub', ruled_by_av: false });
        }
      }

      return {
        ...prev,
        manual_documents: updatedDocs
      };
    });
  };

  const addManualDocument = () => {
    setFormData((prev) => ({
      ...prev,
      manual_documents: [...prev.manual_documents, { type: 'pay_stub', ruled_by_av: false }]
    }));
  };

  const removeManualDocument = (index: number) => {
    const updatedDocs = formData.manual_documents.filter((_, i) => i !== index);

    const hasNonAVDoc = updatedDocs.some((doc) => !doc.ruled_by_av);
    if (!hasNonAVDoc && updatedDocs.length > 0) {
      updatedDocs[0].ruled_by_av = false;
      updatedDocs[0].av_result = undefined;
    }

    setFormData((prev) => ({
      ...prev,
      manual_documents: updatedDocs
    }));
  };

  useEffect(() => {
    if (propertyIsIdle) {
      onGetPropertiesDDown({
        page: 0,
        rowsPerPage: 20,
        filter: 'status!=ARCHIVED'
      });
    }
  }, [propertyIsIdle, onGetPropertiesDDown]);

  useEffect(() => {
    if (propertyCount > 0 && propertyData && propertyData.length < propertyCount) {
      onGetPropertiesDDown({
        page: 0,
        rowsPerPage: propertyCount,
        filter: 'status!=ARCHIVED'
      });
    }
  }, [propertyCount, propertyData, onGetPropertiesDDown]);

  useEffect(() => {
    if (propertyData) {
      setFilteredProperties(propertyData);
    }
  }, [propertyData]);

  // ITL-3253 simulated Gamma Ray.
  useEffect(() => {
    if (!gammaRayOpen || gammaRayCatalog) return;

    const loadGammaRayCatalog = async () => {
      try {
        const response = await fetch(
          `${configVars.api_url}/dev_dashboard/document_evaluation_checks`,
          { credentials: 'include' }
        );

        const contentType = response.headers.get('content-type');
        if (!contentType?.includes('application/json')) {
          throw new Error('Server returned non-JSON response');
        }

        const result = await response.json();

        if (!response.ok) {
          setMessage({ type: 'error', text: `API Error: ${result?.error || 'Unknown error'}` });
          return;
        }

        const catalog = result as GammaRayCatalog;
        setGammaRayCatalog(catalog);

        const preset =
          catalog.scenarios.find((scenario) => scenario.id === gammaRayScenarioId) ??
          catalog.scenarios[0];

        if (preset) {
          setGammaRayScenarioId(preset.id);
          setGammaRayRows(gammaRayDraftRows(preset.rows));
        }
      } catch (error) {
        setMessage({
          type: 'error',
          text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
        });
      }
    };

    loadGammaRayCatalog();
  }, [gammaRayOpen, gammaRayCatalog, gammaRayScenarioId]);

  if (!hasDevDashboardAccess) {
    return (
      <ListView title="Access Denied">
        <div className="Container">
          <S.MessageAlert type="error">
            Access denied. You do not have permission to access the Dev Dashboard.
          </S.MessageAlert>
        </div>
      </ListView>
    );
  }

  if (process.env.SNAPPT_ENV === 'prod') {
    return (
      <ListView title="Not Available">
        <div className="Container">
          <S.MessageAlert type="error">
            Dev Dashboard is not available in production environment.
          </S.MessageAlert>
        </div>
      </ListView>
    );
  }

  const findIvEnabledProperty = () =>
    (propertyData || []).find((p) =>
      p.property_features?.some(
        (f: { code?: string; state?: string }) =>
          f.code === 'income_verification' && f.state === 'enabled'
      )
    );

  // ITL-3002 — VOA needs BOTH income_verification and its dependent
  // asset_verification feature (the consumer's property-feature gate).
  const findVoaEnabledProperty = () =>
    (propertyData || []).find(
      (p) =>
        p.property_features?.some(
          (f: { code?: string; state?: string }) =>
            f.code === 'income_verification' && f.state === 'enabled'
        ) &&
        p.property_features?.some(
          (f: { code?: string; state?: string }) =>
            f.code === 'asset_verification' && f.state === 'enabled'
        )
    );

  const findIvDisputeProperty = () =>
    (propertyData || []).find(
      (p) =>
        p.property_features?.some(
          (f: { code?: string; state?: string }) =>
            f.code === 'income_verification' && f.state === 'enabled'
        ) &&
        p.property_features?.some(
          (f: { code?: string; state?: string }) =>
            f.code === 'payroll_linking' && f.state === 'enabled'
        )
    );

  const submitInsufficientReasonsPreset = async () => {
    const property = (propertyData || [])[0];
    if (!property) {
      setMessage({
        type: 'error',
        text: 'No properties available. Please wait for properties to load.'
      });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const apiUrl = `${configVars.api_url}/test_scenarios`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          property_short_id: property.short_id,
          workflow: 'fraud_detection',
          manual_documents: [
            { type: 'pay_stub', ruled_by_av: false },
            { type: 'offer_letter', ruled_by_av: false }
          ],
          potential_matches: 0,
          reviewer_id: user?.id
        })
      });

      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        throw new Error('Server returned non-JSON response');
      }

      const result = await response.json();

      if (response.ok) {
        const folderId = result.folder_id;
        setMessage({
          type: 'success',
          text: `Insufficient reasons test scenario created on ${property.name}! Redirecting to folder...`
        });
        setTimeout(() => navigate(`/my-work/${folderId}`), 2000);
      } else {
        setMessage({
          type: 'error',
          text: `API Error: ${result?.error || 'Unknown error'}`
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const submitOlvSendReportGatePreset = async () => {
    const property = (propertyData || [])[0];
    if (!property) {
      setMessage({
        type: 'error',
        text: 'No properties available. Please wait for properties to load.'
      });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const apiUrl = `${configVars.api_url}/test_scenarios`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          property_short_id: property.short_id,
          workflow: 'fraud_detection',
          manual_documents: [
            { type: 'pay_stub', ruled_by_av: true, av_result: 'CLEAN' },
            {
              type: 'offer_letter',
              ruled_by_av: false,
              result: 'UNDETERMINED',
              result_insufficient_reason: 'PENDING_EMPLOYER_VERIFICATION'
            }
          ],
          potential_matches: 0,
          reviewer_id: user?.id
        })
      });

      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        throw new Error('Server returned non-JSON response');
      }

      const result = await response.json();

      if (response.ok) {
        const folderId = result.folder_id;
        setMessage({
          type: 'success',
          text: `OLV Send Report Gate scenario created on ${property.name}! Redirecting to folder...`
        });
        setTimeout(() => navigate(`/my-work/${folderId}`), 2000);
      } else {
        setMessage({
          type: 'error',
          text: `API Error: ${result?.error || 'Unknown error'}`
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const submitOlvNonBlockingPreset = async (variant: 'undetermined_scanned' | 'clean') => {
    const property = (propertyData || [])[0];
    if (!property) {
      setMessage({
        type: 'error',
        text: 'No properties available. Please wait for properties to load.'
      });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    const offerLetterDoc =
      variant === 'clean'
        ? { type: 'offer_letter', ruled_by_av: false, result: 'CLEAN' }
        : {
            type: 'offer_letter',
            ruled_by_av: false,
            result: 'UNDETERMINED',
            result_insufficient_reason: 'SCANNED_DOCUMENTS'
          };

    const label = variant === 'clean' ? 'OLV Clean' : 'OLV Undetermined (non-PEV)';

    try {
      const apiUrl = `${configVars.api_url}/test_scenarios`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          property_short_id: property.short_id,
          workflow: 'fraud_detection',
          manual_documents: [
            { type: 'pay_stub', ruled_by_av: true, av_result: 'CLEAN' },
            offerLetterDoc
          ],
          potential_matches: 0,
          reviewer_id: user?.id
        })
      });

      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        throw new Error('Server returned non-JSON response');
      }

      const result = await response.json();

      if (response.ok) {
        const folderId = result.folder_id;
        setMessage({
          type: 'success',
          text: `${label} scenario created on ${property.name}! Redirecting to folder...`
        });
        setTimeout(() => navigate(`/my-work/${folderId}`), 2000);
      } else {
        setMessage({
          type: 'error',
          text: `API Error: ${result?.error || 'Unknown error'}`
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const submitSimulateOlApplicantSubmission = async () => {
    const property = (propertyData || [])[0];
    if (!property) {
      setMessage({
        type: 'error',
        text: 'No properties available. Please wait for properties to load.'
      });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const apiUrl = `${configVars.api_url}/test_scenarios`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          property_short_id: property.short_id,
          workflow: 'fraud_detection',
          manual_documents: [{ type: 'offer_letter', ruled_by_av: false }],
          potential_matches: 0,
          reviewer_id: user?.id,
          simulate_olv_ingestion: true
        })
      });

      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        throw new Error('Server returned non-JSON response');
      }

      const result = await response.json();

      if (response.ok) {
        const folderId = result.folder_id;
        setMessage({
          type: 'success',
          text: `OL applicant submission simulated on ${property.name}! Redirecting to folder...`
        });
        setTimeout(() => navigate(`/my-work/${folderId}`), 2000);
      } else {
        setMessage({
          type: 'error',
          text: `API Error: ${result?.error || 'Unknown error'}`
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const submitIvPreset = async (ivAutomationType: 'mixed' | 'none' | 'all_automated') => {
    const ivProperty = findIvEnabledProperty();
    if (!ivProperty) {
      setMessage({
        type: 'error',
        text: 'No property with the income_verification feature enabled was found.'
      });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const apiUrl = `${configVars.api_url}/test_scenarios`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          property_short_id: ivProperty.short_id,
          workflow: 'income_verification',
          manual_documents: [{ type: 'pay_stub', ruled_by_av: false }],
          potential_matches: 0,
          iv_automation_type: ivAutomationType
        })
      });

      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        throw new Error('Server returned non-JSON response');
      }

      const result = await response.json();

      if (response.ok) {
        const reviewId = result.income_verification_review_id;
        const entryId = result.entry_id;

        if (reviewId && entryId) {
          setMessage({
            type: 'success',
            text: `IV scenario created on ${ivProperty.name}! Redirecting to IV review...`
          });
          setTimeout(() => navigate(`/my-work/incomeVerification/${reviewId}/${entryId}`), 2000);
        } else {
          setMessage({
            type: 'success',
            text: `IV scenario created on ${ivProperty.name}! Redirecting to My Work...`
          });
          setTimeout(() => navigate('/my-work'), 2000);
        }
      } else {
        setMessage({
          type: 'error',
          text: `API Error: ${result?.error || 'Unknown error'}`
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ITL-2706 — Route to specialty queue. Seeds an IV submission with specialty
  // doc proofs and a scheduled :specialty_income ReviewItem (bypasses the LD
  // gate for fast dev iteration). Redirects to /my-work so the analyst can
  // pull from the queue — specialty-capable reviewer picks it up, regular IV
  // reviewer does not (strict no-fallback).
  const submitSpecialtyQueuePreset = async () => {
    const ivProperty = findIvEnabledProperty();
    if (!ivProperty) {
      setMessage({
        type: 'error',
        text: 'No property with the income_verification feature enabled was found.'
      });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const apiUrl = `${configVars.api_url}/test_scenarios`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          property_short_id: ivProperty.short_id,
          workflow: 'income_verification',
          // 3 specialty docs so the auto-calc grid renders a realistic multi-row
          // set (each proof maps to an upload row in the dev calc mock).
          manual_documents: [
            { type: 'cash_app_statement', ruled_by_av: false },
            { type: 'cash_app_statement', ruled_by_av: false },
            { type: 'cash_app_statement', ruled_by_av: false }
          ],
          potential_matches: 0,
          iv_automation_type: 'specialty_queue'
        })
      });

      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        throw new Error('Server returned non-JSON response');
      }

      const result = await response.json();

      if (response.ok) {
        setMessage({
          type: 'success',
          text: `Specialty queue item created on ${ivProperty.name}! Redirecting to /my-work — pick it up as a specialty-capable reviewer.`
        });
        setTimeout(() => navigate('/my-work'), 2000);
      } else {
        setMessage({
          type: 'error',
          text: `API Error: ${result?.error || 'Unknown error'}`
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ITL-2947 — Trigger-2 error-code routing through the real gate (no bypass,
  // unlike specialty_queue). Requires the enhanced_financial property feature
  // enabled for the property.
  // Codes mirror ErrorCodeMatrix.routed_codes() (error_code_matrix.ex) — the
  // backend list is the source of truth.
  const submitTrigger2ErrorCodePreset = async (errorCode: 3001 | 3003 | 3004) => {
    // Fixture-composition choice for these presets, NOT a routing constraint —
    // 3001/3003 route for any source; these buttons just build them on a
    // bank-linking submission, which needs the property feature.
    const presetUsesBankLinkingDocs = errorCode !== 3004;
    const property = (propertyData || []).find((p) => {
      const enabled = (code: string) =>
        p.property_features?.some(
          (f: { code?: string; state?: string }) => f.code === code && f.state === 'enabled'
        );
      return (
        enabled('income_verification') && (!presetUsesBankLinkingDocs || enabled('bank_linking'))
      );
    });
    if (!property) {
      setMessage({
        type: 'error',
        text: presetUsesBankLinkingDocs
          ? 'No property with income_verification + bank_linking features enabled was found.'
          : 'No property with the income_verification feature enabled was found.'
      });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const apiUrl = `${configVars.api_url}/test_scenarios`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          property_short_id: property.short_id,
          workflow: 'income_verification',
          iv_automation_type: 'specialty_error_code',
          error_code: errorCode,
          potential_matches: 0,
          // 3004 needs a paystub-only composition with 2+ paystub proofs (the
          // matrix counts them); 3001/3003 use a bank-linking source (no proofs;
          // 1 doc suffices — the matrix keys on source presence, not doc count).
          ...(errorCode === 3004
            ? {
                manual_documents: [
                  { type: 'pay_stub', ruled_by_av: false },
                  { type: 'pay_stub', ruled_by_av: false }
                ]
              }
            : { manual_documents: [], bank_linking_docs: 1 })
        })
      });

      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        throw new Error('Server returned non-JSON response');
      }

      const result = await response.json();

      if (response.ok) {
        setMessage({
          type: 'success',
          text: `Error-code ${errorCode} scenario created on ${property.name}. If the Enhanced Financial property feature is enabled for it, the entry is now held in the specialty queue — redirecting to /my-work.`
        });
        setTimeout(() => navigate('/my-work'), 2000);
      } else {
        setMessage({
          type: 'error',
          text: `API Error: ${result?.error || 'Unknown error'}`
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ITL-2708 — Specialty Income Calc page. Seeds an IV scenario then redirects
  // directly to the new page using its entry_id (bypasses queue routing so you
  // can iterate on the page UI without going through assignment).
  const submitSpecialtyIncomeCalcPreset = async () => {
    const ivProperty = findIvEnabledProperty();
    if (!ivProperty) {
      setMessage({
        type: 'error',
        text: 'No property with the income_verification feature enabled was found.'
      });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const apiUrl = `${configVars.api_url}/test_scenarios`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          property_short_id: ivProperty.short_id,
          workflow: 'income_verification',
          manual_documents: [{ type: 'pay_stub', ruled_by_av: false }],
          potential_matches: 0,
          iv_automation_type: 'none'
        })
      });

      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        throw new Error('Server returned non-JSON response');
      }

      const result = await response.json();

      if (response.ok) {
        const entryId = result.entry_id;
        if (entryId) {
          // Placeholder reviewItemId until T1 (ITL-2706) creates real ReviewItems.
          setMessage({
            type: 'success',
            text: `Specialty Income Calc scenario seeded on ${ivProperty.name}! Redirecting…`
          });
          setTimeout(
            () => navigate(`/my-work/specialty-income-calc/dev-review-item/${entryId}`),
            2000
          );
        } else {
          setMessage({
            type: 'error',
            text: 'Scenario created but no entry_id returned.'
          });
        }
      } else {
        setMessage({
          type: 'error',
          text: `API Error: ${result?.error || 'Unknown error'}`
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  // FVS-backed screenshot staging handlers.
  const loadVoaScreenshotCandidates = async () => {
    setVoaCandidatesLoading(true);
    setMessage(null);

    try {
      const response = await fetch(
        `${configVars.api_url}/test_scenarios/voa_screenshot_candidates`,
        { credentials: 'include' }
      );
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.error || 'Failed to load candidates');
      }

      // Zero-extraction candidates first: they are the rarest and the ones the
      // review page must render without any FVS structure.
      const candidates: VoaScreenshotCandidate[] = [...(result.data ?? [])].sort(
        (a, b) => Number(isZeroExtraction(b)) - Number(isZeroExtraction(a))
      );
      setVoaCandidates(candidates);
      setSelectedVoaCandidate(candidates[0]?.applicant_id ?? '');

      if (candidates.length === 0) {
        setMessage({
          type: 'error',
          text: 'No FVS-backed applicants found — complete at least one real VOA review first.'
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setVoaCandidatesLoading(false);
    }
  };

  const stageVoaScreenshotReview = async () => {
    const candidate = voaCandidates.find((c) => c.applicant_id === selectedVoaCandidate);
    if (!candidate) return;

    setIsLoading(true);
    setMessage(null);

    try {
      const response = await fetch(`${configVars.api_url}/test_scenarios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          workflow: 'asset_verification',
          fvs_backed_applicant_id: candidate.applicant_id
        })
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.error || 'Failed to stage review');
      }

      setMessage({ type: 'success', text: 'Screenshot review staged! Redirecting…' });
      setTimeout(
        () =>
          navigate(
            `/my-work/asset-verification/${result.asset_verification_review_item_id}/${result.entry_id}`
          ),
        1500
      );
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ITL-3002 — Simulate FVS handlers.
  const submitVoaSeededScenario = async (assignToCaller: boolean) => {
    const voaProperty = findVoaEnabledProperty();
    if (!voaProperty) {
      setMessage({
        type: 'error',
        text: 'No property with income_verification + asset_verification enabled — enable both PropertyFeatures on a dev property first.'
      });
      return;
    }

    const fixture = VOA_FIXTURES.find((f) => f.id === voaFixture) ?? VOA_FIXTURES[0];

    setIsLoading(true);
    setMessage(null);

    try {
      const response = await fetch(`${configVars.api_url}/test_scenarios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          property_short_id: voaProperty.short_id,
          workflow: 'asset_verification',
          manual_documents: Array.from({ length: fixture.documents }, () => ({
            type: voaProofType,
            ruled_by_av: false
          })),
          fvs_fixture: voaFixture,
          claim_response: voaClaimResponse,
          complete_response: voaCompleteResponse,
          assign_to_caller: assignToCaller
        })
      });

      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        throw new Error('Server returned non-JSON response');
      }

      const result = await response.json();

      if (response.ok) {
        const scenario: VoaSeededScenario = {
          entryId: result.entry_id,
          applicantId: result.applicant_id,
          reviewItemId: result.asset_verification_review_item_id ?? null,
          fixture: result.fvs_fixture ?? voaFixture
        };
        setVoaScenario(scenario);

        if (assignToCaller && scenario.reviewItemId) {
          setMessage({
            type: 'success',
            text: `VOA scenario (${scenario.fixture}) seeded on ${voaProperty.name} and assigned to you! Redirecting…`
          });
          setTimeout(
            () =>
              navigate(`/my-work/asset-verification/${scenario.reviewItemId}/${scenario.entryId}`),
            2000
          );
        } else {
          setMessage({
            type: 'success',
            text: `VOA scenario (${scenario.fixture}) seeded on ${voaProperty.name} for the event path — publish the needs-review event next.`
          });
        }
      } else {
        setMessage({
          type: 'error',
          text: `API Error: ${result?.error || 'Unknown error'}`
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const applyGammaRayScenario = (scenarioId: string) => {
    setGammaRayScenarioId(scenarioId);

    const preset = gammaRayCatalog?.scenarios.find((scenario) => scenario.id === scenarioId);
    if (preset) setGammaRayRows(gammaRayDraftRows(preset.rows));
  };

  const updateGammaRayRow = (engineKey: string, changes: Partial<GammaRayRowDraft>) => {
    setGammaRayRows((rows) =>
      rows.map((row) => (row.engine_key === engineKey ? { ...row, ...changes } : row))
    );
  };

  const updateGammaRayCandidates = (
    engineKey: string,
    update: (candidates: GammaRayCandidateDraft[]) => GammaRayCandidateDraft[]
  ) => {
    setGammaRayRows((rows) =>
      rows.map((row) =>
        row.engine_key === engineKey ? { ...row, candidates: update(row.candidates) } : row
      )
    );
  };

  const addGammaRayCandidate = (engineKey: string) => {
    const cap = gammaRayCatalog?.max_candidates ?? 5;

    updateGammaRayCandidates(engineKey, (candidates) =>
      candidates.length >= cap ? candidates : [...candidates, { score: '0.5' }]
    );
  };

  const removeGammaRayCandidate = (engineKey: string, index: number) => {
    updateGammaRayCandidates(engineKey, (candidates) =>
      candidates.filter((_candidate, i) => i !== index)
    );
  };

  const updateGammaRayCandidate = (engineKey: string, index: number, value: string) => {
    updateGammaRayCandidates(engineKey, (candidates) =>
      candidates.map((candidate, i) => (i === index ? { ...candidate, score: value } : candidate))
    );
  };

  // Coming back from not-run seeds one candidate on the bar, so the row reads
  // as a plain match until the tester edits it.
  const toggleGammaRayNotRun = (engineKey: string, notRun: boolean) => {
    const check = gammaRayCatalog?.checks.find((c) => c.engine_key === engineKey);

    updateGammaRayRow(
      engineKey,
      notRun
        ? { not_run_reason: gammaRayCatalog?.not_run_reasons[0] ?? null, candidates: [] }
        : { not_run_reason: null, candidates: [{ score: String(check?.match_bar ?? 0.9) }] }
    );
  };

  // A fresh entry defaults to evaluating every document on it, which is what
  // the real pipeline will do once ITL-3254 publishes per document.
  const selectGammaRayEntry = (entry: GammaRayEntry) => {
    setGammaRayEntry(entry);
    setGammaRayProofIds(entry.proofs.map((proof) => proof.proof_id));
  };

  const setGammaRayResult = (check: GammaRayCheck, result: GammaRayResult) => {
    updateGammaRayCandidates(check.engine_key, () =>
      gammaRayResultScores(check, result).map((score) => ({ score }))
    );
  };

  // A preset that leaves a trend check out has the simulator answer it
  // consistent, so a missing row reads as Not edited.
  const gammaRayTrendTemplate = (check: GammaRayTrendCheck): string =>
    gammaRayRows.find((r) => r.engine_key === check.engine_key)?.template ??
    check.templates[0] ??
    '';

  const gammaRayTrendValue = (engineKey: string): string => {
    const row = gammaRayRows.find((r) => r.engine_key === engineKey);
    if (row?.not_run_reason) return `${GAMMA_RAY_NOT_RUN}${row.not_run_reason}`;
    return row?.variant ?? 'consistent';
  };

  const setGammaRayTrend = (check: GammaRayTrendCheck, template: string, value: string) => {
    const notRun = value.startsWith(GAMMA_RAY_NOT_RUN);
    const engineKey = check.engine_key;
    const row: GammaRayRowDraft = {
      engine_key: engineKey,
      candidates: [],
      not_run_reason: notRun ? value.slice(GAMMA_RAY_NOT_RUN.length) : null,
      template,
      variant: notRun ? null : value
    };

    setGammaRayRows((rows) =>
      rows.some((r) => r.engine_key === engineKey)
        ? rows.map((r) => (r.engine_key === engineKey ? row : r))
        : [...rows, row]
    );
  };

  const toggleGammaRayProof = (proofId: string) => {
    setGammaRayProofIds((ids) =>
      ids.includes(proofId) ? ids.filter((id) => id !== proofId) : [...ids, proofId]
    );
  };

  const gammaRayProperty = () =>
    formData.property_short_id || (propertyData || [])[0]?.short_id || '';

  const createGammaRayEntry = async () => {
    const propertyShortId = gammaRayProperty();

    if (!propertyShortId) {
      setMessage({ type: 'error', text: 'No property available to seed an entry on.' });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const response = await fetch(
        `${configVars.api_url}/dev_dashboard/document_evaluation_entry`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ property_short_id: propertyShortId })
        }
      );

      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        throw new Error('Server returned non-JSON response');
      }

      const result = await response.json();

      if (!response.ok) {
        setMessage({ type: 'error', text: `API Error: ${result?.error || 'Unknown error'}` });
        return;
      }

      const entry = result as GammaRayEntry;
      selectGammaRayEntry(entry);
      setMessage({
        type: 'success',
        text: `Seeded entry ${entry.entry_id} with ${entry.proof_count} documents, assigned to you. Publish to evaluate them.`
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const publishGammaRayResults = async () => {
    setIsLoading(true);
    setMessage(null);

    try {
      const response = await fetch(
        `${configVars.api_url}/dev_dashboard/simulate_document_evaluation`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            entry_id: gammaRayEntry?.entry_id,
            proof_ids: gammaRayProofIds,
            checks: gammaRayWireRows(gammaRayRows)
          })
        }
      );

      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        throw new Error('Server returned non-JSON response');
      }

      const result = await response.json();

      if (response.ok) {
        const requested = (result as GammaRayRequested).requested?.length ?? 0;
        const folderId = gammaRayEntry?.folder_id;

        setMessage({
          type: 'success',
          text: `Requested an evaluation for ${requested} document${
            requested === 1 ? '' : 's'
          }. The simulated engine answers on the queue in a few seconds. Redirecting to your review...`
        });

        if (folderId) setTimeout(() => navigate(`/my-work/${folderId}`), 2000);
      } else {
        setMessage({ type: 'error', text: `API Error: ${result?.error || 'Unknown error'}` });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const publishVoaNeedsReview = async () => {
    if (!voaScenario) return;

    setIsLoading(true);
    setMessage(null);

    try {
      const response = await fetch(`${configVars.api_url}/dev_dashboard/publish_voa_needs_review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ applicant_id: voaScenario.applicantId })
      });

      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        throw new Error('Server returned non-JSON response');
      }

      const result = await response.json();

      if (response.ok) {
        setMessage({
          type: 'success',
          text: `needs_review event ${result.event_id} published — stay online in My Work and watch for the assignment (or the archive, if you selected a claim refusal).`
        });
      } else if (response.status === 404) {
        // stale localStorage handle (e.g. after a DB reset) — clear it so the
        // section recovers instead of dead-ending on a scenario that is gone
        setVoaScenario(null);
        setMessage({
          type: 'error',
          text: 'That scenario no longer exists (database reset?) — handle cleared, seed a new one.'
        });
      } else {
        setMessage({
          type: 'error',
          text: `API Error: ${result?.error || 'Unknown error'}`
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetVoaScenario = async () => {
    if (!voaScenario) return;

    setIsLoading(true);
    setMessage(null);

    try {
      const response = await fetch(`${configVars.api_url}/dev_dashboard/reset_voa_scenario`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ applicant_id: voaScenario.applicantId })
      });

      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        throw new Error('Server returned non-JSON response');
      }

      const result = await response.json();

      if (response.ok) {
        setVoaScenario(null);
        setMessage({
          type: 'success',
          text: `VOA scenario reset — item ${
            result.archived_review_item_id ?? 'none'
          } archived, queue row ${
            result.queue_item_reset ? 'freed' : 'untouched'
          }. Seed again to rerun.`
        });
      } else if (response.status === 404) {
        setVoaScenario(null);
        setMessage({
          type: 'error',
          text: 'That scenario no longer exists (database reset?) — handle cleared, seed a new one.'
        });
      } else {
        setMessage({
          type: 'error',
          text: `API Error: ${result?.error || 'Unknown error'}`
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const submitIvScenarioPreset = async (config: {
    body: Record<string, unknown>;
    onSuccess: (result: Record<string, unknown>, propertyName: string) => void;
  }) => {
    if (isLoadingProperties || (propertyCount > 0 && (propertyData || []).length < propertyCount)) {
      setMessage({
        type: 'error',
        text: 'Properties are still loading. Please wait a moment and try again.'
      });
      return;
    }

    const ivProperty = findIvDisputeProperty();
    if (!ivProperty) {
      setMessage({
        type: 'error',
        text: 'No property with both income_verification and payroll_linking features enabled was found.'
      });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const apiUrl = `${configVars.api_url}/test_scenarios`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          property_short_id: ivProperty.short_id,
          workflow: 'income_verification',
          ...config.body
        })
      });

      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        throw new Error('Server returned non-JSON response');
      }

      const result = await response.json();

      if (response.ok) {
        config.onSuccess(result, ivProperty.name);
      } else {
        setMessage({
          type: 'error',
          text: `API Error: ${result?.error || 'Unknown error'}`
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const submitIvDisputePreset = () =>
    submitIvScenarioPreset({
      body: {
        manual_documents: [
          { type: 'pay_stub', ruled_by_av: false },
          { type: 'pay_stub', ruled_by_av: false },
          { type: 'pay_stub', ruled_by_av: false }
        ],
        connected_payroll_docs: 2,
        potential_matches: 0,
        iv_automation_type: 'completed_dispute'
      },
      onSuccess: (result, propertyName) => {
        const folderId = result.folder_id;
        setMessage({
          type: 'success',
          text: `IV dispute scenario created on ${propertyName}! Redirecting to folder...`
        });
        setTimeout(() => navigate(`/folder/${folderId}`), 2000);
      }
    });

  const submitPayrollOnlyDisputePreset = () =>
    submitIvScenarioPreset({
      body: {
        manual_documents: [],
        connected_payroll_docs: 2,
        potential_matches: 0,
        iv_automation_type: 'completed_dispute'
      },
      onSuccess: (result, propertyName) => {
        const folderId = result.folder_id;
        setMessage({
          type: 'success',
          text: `Payroll-only dispute scenario created on ${propertyName}! Redirecting to folder...`
        });
        setTimeout(() => navigate(`/folder/${folderId}`), 2000);
      }
    });

  const createPresetScenario = (presetType: string) => {
    let presetData: Partial<TestScenarioParams> = {};

    switch (presetType) {
      case 'basic_fraud':
        presetData = {
          workflow: 'fraud_detection',
          manual_documents: [{ type: 'pay_stub', ruled_by_av: false }],
          potential_matches: 0
        };
        break;
      case 'av_clean':
        presetData = {
          workflow: 'fraud_detection',
          manual_documents: [
            { type: 'pay_stub', ruled_by_av: true, av_result: 'CLEAN' },
            { type: 'bank_statement', ruled_by_av: false }
          ],
          potential_matches: 0
        };
        break;
      case 'with_matches':
        presetData = {
          workflow: 'fraud_detection',
          manual_documents: [{ type: 'pay_stub', ruled_by_av: true, av_result: 'CLEAN' }],
          potential_matches: 0
        };
        break;
      case 'olv_lock_gate':
        presetData = {
          workflow: 'fraud_detection',
          manual_documents: [
            { type: 'pay_stub', ruled_by_av: false },
            { type: 'offer_letter', ruled_by_av: false }
          ],
          potential_matches: 0
        };
        break;
    }

    setFormData((prev) => ({ ...prev, ...presetData }));
    setMessage({
      type: 'success',
      text: `Loaded ${presetType.replace(
        '_',
        ' '
      )} preset. Don't forget to set a valid property short ID!`
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    const documentsToSubmit = [...formData.manual_documents];
    const nonAVDocs = documentsToSubmit.filter((doc) => !doc.ruled_by_av);
    if (nonAVDocs.length === 0) {
      documentsToSubmit.push({ type: 'pay_stub', ruled_by_av: false });
    }

    try {
      const requestPayload: {
        property_short_id: string;
        workflow: string;
        manual_documents: ManualDocument[];
        potential_matches: number;
        reviewer_id?: string;
      } = {
        property_short_id: formData.property_short_id,
        workflow: formData.workflow,
        manual_documents: documentsToSubmit,
        potential_matches: formData.potential_matches
      };

      if (formData.reviewer_id && formData.reviewer_id.trim()) {
        requestPayload.reviewer_id = formData.reviewer_id.trim();
      } else if (user?.id) {
        requestPayload.reviewer_id = user.id;
      }

      const apiUrl = `${configVars.api_url}/test_scenarios`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(requestPayload)
      });

      let result;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        result = await response.json();
      } else {
        const textResponse = await response.text();
        console.log('Non-JSON response received:', textResponse);
        throw new Error(`Server returned non-JSON response: ${textResponse.substring(0, 200)}...`);
      }

      if (response.ok) {
        const entryId = result.entry_id;
        const folderId = result.folder_id;

        if (redirectToReview && folderId) {
          setMessage({
            type: 'success',
            text: 'Review scenario created successfully! Redirecting to review...'
          });

          setTimeout(() => {
            navigate(`/my-work/${folderId}`);
          }, 2000);
          return;
        }

        setMessage({
          type: 'success',
          text: `Review scenario created successfully! Entry ID: ${
            entryId || 'Unknown'
          }, Folder ID: ${folderId || 'Unknown'}`
        });

        setFormData({
          property_short_id: '',
          workflow: 'fraud_detection',
          manual_documents: [{ type: 'pay_stub', ruled_by_av: false }],
          potential_matches: 0,
          reviewer_id: ''
        });
      } else {
        if (response.status === 401) {
          setMessage({
            type: 'error',
            text: 'Unauthorized: Please check your login and role permissions. Required: Admin, FraudAnalyst, or SeniorFraudAnalyst'
          });
        } else if (response.status === 400) {
          setMessage({
            type: 'error',
            text: `Bad Request: ${
              result?.error || 'Invalid request data. Please check your inputs.'
            }`
          });
        } else {
          setMessage({
            type: 'error',
            text: `API Error: ${result?.error || 'Unknown error occurred'}`
          });
        }
      }
    } catch (error) {
      console.error('Review scenario creation error:', error);
      setMessage({
        type: 'error',
        text: `Error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignalSubmit = async () => {
    setSignalLoading(true);
    setSignalResult(null);
    setMessage(null);

    try {
      const apiUrl = `${configVars.api_url}/signal_scenarios`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          signal_type: 'application_pattern',
          condition: signalCondition,
          options: {
            // Pass current user's email to generate plus-addressed variations
            // This allows the user to receive OTP verification codes in applicant-experience
            user_email: user?.email
          }
        })
      });

      const contentType = response.headers.get('Content-Type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Server returned non-JSON response');
      }

      const data = await response.json();

      if (response.ok) {
        setSignalResult(data);
        setMessage({
          type: 'success',
          text: data.message
        });
      } else {
        setMessage({
          type: 'error',
          text: data.error || 'Failed to create signal scenario'
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'An error occurred'
      });
    } finally {
      setSignalLoading(false);
    }
  };

  const handleCopyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleSignalCleanup = async () => {
    setCleanupLoading(true);
    setMessage(null);

    try {
      const apiUrl = `${configVars.api_url}/signal_scenarios`;
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          // Clean up scenarios created with the current user's email
          user_email: user?.email
        })
      });

      const contentType = response.headers.get('Content-Type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Server returned non-JSON response');
      }

      const data = await response.json();

      if (response.ok) {
        setSignalResult(null); // Clear any previous result
        setMessage({
          type: 'success',
          text: data.message || `Cleaned up ${data.deleted_count} entries`
        });
      } else {
        setMessage({
          type: 'error',
          text: data.error || 'Failed to clean up signal scenarios'
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'An error occurred during cleanup'
      });
    } finally {
      setCleanupLoading(false);
    }
  };

  const getTriggerApplicantLink = () => {
    if (!signalResult) return '';
    return getPublicLink(
      signalResult.trigger_property.short_id,
      signalResult.trigger_property.company_short_id
    );
  };

  return (
    <ListView title="Dev Dashboard">
      <div className="Container">
        {user && (
          <S.UserInfoCard>
            <S.UserInfoHeader>
              <S.UserAvatar>
                {user.first_name?.[0]}
                {user.last_name?.[0]}
              </S.UserAvatar>
              <S.UserDetails>
                <S.UserName>
                  {user.first_name} {user.last_name}
                </S.UserName>
                <S.UserEmail>{user.email}</S.UserEmail>
              </S.UserDetails>
            </S.UserInfoHeader>

            <S.UserRoleSection>
              <div>
                <span style={{ color: '#4A5568', marginRight: '0.5rem' }}>Role:</span>
                <S.UserRoleBadge>{user.role}</S.UserRoleBadge>
              </div>
              <S.RoleRequirement>
                <span>Required:</span> Admin, FraudAnalyst, or SeniorFraudAnalyst
              </S.RoleRequirement>
            </S.UserRoleSection>
          </S.UserInfoCard>
        )}

        {message && <S.MessageAlert type={message.type}>{message.text}</S.MessageAlert>}

        {/* Tab Navigation */}
        <S.TabContainer>
          <S.Tab isActive={activeTab === 'review'} onClick={() => setActiveTab('review')}>
            Review Scenarios
          </S.Tab>
          <S.Tab isActive={activeTab === 'signals'} onClick={() => setActiveTab('signals')}>
            Signal Scenarios
          </S.Tab>
        </S.TabContainer>

        <S.TabContent>
          {/* Review Scenarios Tab */}
          {activeTab === 'review' && (
            <>
              <S.FormSection>
                <S.SectionTitle>Quick Scenario Presets</S.SectionTitle>
                <p style={{ fontSize: '1rem', color: '#4A5568', marginBottom: '1.5rem' }}>
                  Load common review scenarios with one click
                </p>
                <S.PresetButtonsGrid>
                  <S.PresetButton
                    type="button"
                    onClick={() => createPresetScenario('basic_fraud')}
                    color="#3B82F6"
                  >
                    Basic Fraud Review
                  </S.PresetButton>
                  <S.PresetButton
                    type="button"
                    onClick={() => createPresetScenario('av_clean')}
                    color="#3B82F6"
                  >
                    AV Clean Documents
                  </S.PresetButton>
                  <S.PresetButton
                    type="button"
                    onClick={() => createPresetScenario('with_matches')}
                    color="#3B82F6"
                  >
                    With Potential Matches
                  </S.PresetButton>
                  <S.PresetButton
                    type="button"
                    onClick={() => createPresetScenario('olv_lock_gate')}
                    color="#3B82F6"
                  >
                    OLV Lock Gate
                  </S.PresetButton>
                  <S.PresetButton
                    type="button"
                    onClick={submitInsufficientReasonsPreset}
                    disabled={isLoading}
                    color="#8B5CF6"
                    data-testid="dev-preset-insufficient-reasons"
                  >
                    {isLoading ? 'Creating...' : 'Insufficient Reasons (pay stub + offer letter)'}
                  </S.PresetButton>
                  <S.PresetButton
                    type="button"
                    onClick={submitOlvSendReportGatePreset}
                    disabled={isLoading}
                    color="#8B5CF6"
                  >
                    {isLoading ? 'Creating...' : 'OLV Send Report Gate (pending verification)'}
                  </S.PresetButton>
                  <S.PresetButton
                    type="button"
                    onClick={() => submitOlvNonBlockingPreset('undetermined_scanned')}
                    disabled={isLoading}
                    color="#8B5CF6"
                  >
                    {isLoading ? 'Creating...' : 'OLV Non-Blocking (undetermined, scanned docs)'}
                  </S.PresetButton>
                  <S.PresetButton
                    type="button"
                    onClick={() => submitOlvNonBlockingPreset('clean')}
                    disabled={isLoading}
                    color="#8B5CF6"
                  >
                    {isLoading ? 'Creating...' : 'OLV Non-Blocking (clean)'}
                  </S.PresetButton>
                  <S.PresetButton
                    type="button"
                    onClick={submitSimulateOlApplicantSubmission}
                    disabled={isLoading}
                    color="#8B5CF6"
                    data-testid="dev-preset-simulate-ol-applicant-submission"
                  >
                    {isLoading
                      ? 'Creating...'
                      : 'Simulate OL applicant submission (Phase 1 umbrella test)'}
                  </S.PresetButton>
                  <S.PresetButton
                    type="button"
                    onClick={() => submitIvPreset('mixed')}
                    disabled={isLoading}
                    color="#10B981"
                  >
                    {isLoading ? 'Creating...' : 'IV: Mixed Automation (2 auto, 1 manual)'}
                  </S.PresetButton>
                  <S.PresetButton
                    type="button"
                    onClick={() => submitIvPreset('none')}
                    disabled={isLoading}
                    color="#10B981"
                  >
                    {isLoading ? 'Creating...' : 'IV: No Automation (2 manual)'}
                  </S.PresetButton>
                  <S.PresetButton
                    type="button"
                    onClick={() => submitIvPreset('all_automated')}
                    disabled={isLoading}
                    color="#10B981"
                  >
                    {isLoading ? 'Creating...' : 'IV: Fully Automated (3 auto, 0 manual)'}
                  </S.PresetButton>
                  <S.PresetButton
                    type="button"
                    onClick={submitIvDisputePreset}
                    disabled={isLoading}
                    color="#F59E0B"
                  >
                    {isLoading ? 'Creating...' : 'IV: Calculation Dispute (3 pay stubs)'}
                  </S.PresetButton>
                  <S.PresetButton
                    type="button"
                    onClick={submitPayrollOnlyDisputePreset}
                    disabled={isLoading}
                    color="#F59E0B"
                    data-testid="dev-preset-payroll-only-dispute"
                  >
                    {isLoading ? 'Creating...' : 'IV: Connected Payroll Only (Dispute)'}
                  </S.PresetButton>
                  <S.PresetButton
                    type="button"
                    onClick={submitSpecialtyQueuePreset}
                    disabled={isLoading}
                    color="#EC4899"
                    data-testid="dev-preset-specialty-queue"
                  >
                    {isLoading ? 'Creating...' : 'IV: Route to Specialty Queue (ITL-2706)'}
                  </S.PresetButton>
                  <S.PresetButton
                    type="button"
                    onClick={submitSpecialtyIncomeCalcPreset}
                    disabled={isLoading}
                    color="#EC4899"
                    data-testid="dev-preset-specialty-income-calc"
                  >
                    {isLoading ? 'Creating...' : 'IV: Specialty Income Calc Page (ITL-2708)'}
                  </S.PresetButton>
                  <S.PresetButton
                    type="button"
                    onClick={() => submitTrigger2ErrorCodePreset(3001)}
                    disabled={isLoading}
                    color="#8B5CF6"
                    data-testid="dev-preset-trigger2-3001"
                  >
                    {isLoading
                      ? 'Creating...'
                      : 'IV: Specialty via 3001 — BL no gross pay (ITL-2947)'}
                  </S.PresetButton>
                  <S.PresetButton
                    type="button"
                    onClick={() => submitTrigger2ErrorCodePreset(3003)}
                    disabled={isLoading}
                    color="#8B5CF6"
                    data-testid="dev-preset-trigger2-3003"
                  >
                    {isLoading
                      ? 'Creating...'
                      : 'IV: Specialty via 3003 — name mismatch (ITL-2947)'}
                  </S.PresetButton>
                  <S.PresetButton
                    type="button"
                    onClick={() => submitTrigger2ErrorCodePreset(3004)}
                    disabled={isLoading}
                    color="#8B5CF6"
                    data-testid="dev-preset-trigger2-3004"
                  >
                    {isLoading ? 'Creating...' : 'IV: Specialty via 3004 — paystub gap (ITL-2947)'}
                  </S.PresetButton>
                </S.PresetButtonsGrid>
              </S.FormSection>

              <S.FormSection style={{ marginTop: '1.5rem' }}>
                <S.SectionTitle
                  onClick={() => setSimulateFvsOpen((open) => !open)}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  role="button"
                  aria-expanded={simulateFvsOpen}
                  data-testid="dev-voa-simulate-toggle"
                >
                  {simulateFvsOpen ? '▾' : '▸'} Simulate FVS — local dev only
                </S.SectionTitle>
                {simulateFvsOpen && (
                  <>
                    <S.HelpText style={{ marginBottom: '1.5rem' }}>
                      Seeds a real backend VOA scenario served by the simulated FVS client. Local
                      dev only — the simulated client is not wired in deployed dev; use VOA
                      Screenshot Staging below there. Requires a property with income_verification +
                      asset_verification enabled. For the publish path, stay online in My Work so
                      the assignment engine can claim within its next tick.
                    </S.HelpText>

                    <S.FormField>
                      <S.FormLabel>Scenario flow</S.FormLabel>
                      <S.FormSelect
                        value={voaFlow}
                        onChange={(e) => setVoaFlow(e.target.value as VoaFlowId)}
                        data-testid="dev-voa-flow"
                      >
                        {VOA_FLOWS.map((flow) => (
                          <option key={flow.id} value={flow.id}>
                            {flow.label}
                          </option>
                        ))}
                      </S.FormSelect>
                    </S.FormField>

                    <S.FormField>
                      <S.FormLabel>FVS fixture</S.FormLabel>
                      <S.FormSelect
                        value={voaFixture}
                        onChange={(e) => setVoaFixture(e.target.value as VoaFixtureId)}
                        data-testid="dev-voa-fixture"
                      >
                        {VOA_FIXTURES.map((fixture) => (
                          <option key={fixture.id} value={fixture.id}>
                            {fixture.label}
                          </option>
                        ))}
                      </S.FormSelect>
                    </S.FormField>

                    <S.FormField>
                      <S.FormLabel>Seeded proof type</S.FormLabel>
                      <S.FormSelect
                        value={voaProofType}
                        onChange={(e) => setVoaProofType(e.target.value as VoaProofType)}
                        data-testid="dev-voa-proof-type"
                      >
                        {VOA_PROOF_TYPES.map((proofType) => (
                          <option key={proofType.id} value={proofType.id}>
                            {proofType.label}
                          </option>
                        ))}
                      </S.FormSelect>
                    </S.FormField>

                    {voaFlow === 'event' && (
                      <S.FormField>
                        <S.FormLabel>Claim response (what the engine gets)</S.FormLabel>
                        <S.FormSelect
                          value={voaClaimResponse}
                          onChange={(e) => setVoaClaimResponse(e.target.value as VoaClaimResponse)}
                          data-testid="dev-voa-claim-response"
                        >
                          {VOA_CLAIM_RESPONSES.map((option) => (
                            <option key={option.id} value={option.id}>
                              {option.label}
                            </option>
                          ))}
                        </S.FormSelect>
                      </S.FormField>
                    )}

                    <S.FormField>
                      <S.FormLabel>
                        Complete response (applies to submit once ITL-3059 ships)
                      </S.FormLabel>
                      <S.FormSelect
                        value={voaCompleteResponse}
                        onChange={(e) =>
                          setVoaCompleteResponse(e.target.value as VoaCompleteResponse)
                        }
                        data-testid="dev-voa-complete-response"
                      >
                        {VOA_COMPLETE_RESPONSES.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.label}
                          </option>
                        ))}
                      </S.FormSelect>
                    </S.FormField>

                    <div
                      style={{
                        display: 'flex',
                        gap: '1rem',
                        flexWrap: 'wrap',
                        marginBottom: '1rem'
                      }}
                    >
                      {voaFlow === 'direct' && (
                        <S.SubmitButton
                          type="button"
                          onClick={() => submitVoaSeededScenario(true)}
                          disabled={isLoading || !findVoaEnabledProperty()}
                          data-testid="dev-voa-seed-assigned"
                          title={
                            'Seeds applicant + documents and hands you the review item directly — ' +
                            'no event, no consumer, no engine. Opens the VOA review screen. ' +
                            'Use it to work on the screen itself.'
                          }
                        >
                          {isLoading ? 'Working…' : 'Seed & open review ⓘ'}
                        </S.SubmitButton>
                      )}
                      {voaFlow === 'event' && (
                        <>
                          <S.SubmitButton
                            type="button"
                            onClick={() => submitVoaSeededScenario(false)}
                            disabled={isLoading || !findVoaEnabledProperty()}
                            data-testid="dev-voa-seed-for-event"
                            title={
                              'Step 1 — seeds only the applicant + documents, deliberately NO ' +
                              'review item, so the consumer will accept the published event ' +
                              '(a duplicate would be dropped).'
                            }
                          >
                            {isLoading ? 'Working…' : 'Step 1: Seed unassigned ⓘ'}
                          </S.SubmitButton>
                          <S.SubmitButton
                            type="button"
                            onClick={publishVoaNeedsReview}
                            disabled={isLoading || !voaScenario}
                            data-testid="dev-voa-publish"
                            title={
                              'Step 2 — publishes the real needs_review CloudEvent FVS would ' +
                              'send: SNS → SQS → consumer inserts a scheduled item → the engine ' +
                              'claims it via the simulated client. Claim 200 assigns it to you ' +
                              '(stay online in My Work); claim 409/400 archives it.'
                            }
                          >
                            {isLoading ? 'Working…' : 'Step 2: Publish needs_review ⓘ'}
                          </S.SubmitButton>
                        </>
                      )}
                      <S.SubmitButton
                        type="button"
                        onClick={resetVoaScenario}
                        disabled={isLoading || !voaScenario}
                        style={{ backgroundColor: theme.color.red[500] }}
                        data-testid="dev-voa-reset"
                        title={
                          'Archives the active review item (never deletes), frees your reviewer ' +
                          'queue slot and clears the simulation state so the same scenario can ' +
                          'run again.'
                        }
                      >
                        {isLoading ? 'Working…' : 'Reset scenario ⓘ'}
                      </S.SubmitButton>
                    </div>

                    {!findVoaEnabledProperty() && (
                      <S.HelpText data-testid="dev-voa-no-property">
                        No property with income_verification + asset_verification enabled was found
                        — enable both PropertyFeatures on a dev property first.
                      </S.HelpText>
                    )}

                    {voaScenario && voaScenario.reviewItemId === null && voaFlow === 'direct' && (
                      <S.HelpText data-testid="dev-voa-flow-mismatch">
                        An event-flow scenario is seeded but hidden by this mode — switch Scenario
                        flow to publish or reset it.
                      </S.HelpText>
                    )}

                    {voaScenario && (
                      <S.HelpText data-testid="dev-voa-scenario-info">
                        Seeded: entry {voaScenario.entryId} · applicant {voaScenario.applicantId} ·
                        fixture {voaScenario.fixture} · review item{' '}
                        {voaScenario.reviewItemId ?? 'none (event path)'}
                      </S.HelpText>
                    )}
                  </>
                )}
              </S.FormSection>

              <S.FormSection style={{ marginTop: '1.5rem' }}>
                <S.SectionTitle
                  onClick={() => setGammaRayOpen((open) => !open)}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  role="button"
                  aria-expanded={gammaRayOpen}
                  data-testid="dev-gamma-ray-toggle"
                >
                  {gammaRayOpen ? '▾' : '▸'} Simulate Gamma Ray
                </S.SectionTitle>
                {gammaRayOpen && (
                  <>
                    <S.HelpText style={{ marginBottom: '1.5rem' }}>
                      Stands in for the ML engine, never in production. 1. Create a new entry: it
                      seeds a paystub and a bank statement and assigns the review to you. 2. Pick
                      the documents to evaluate. 3. Choose what each check answers below, then
                      Request evaluation. Results land a few seconds later and you are taken to the
                      review.
                    </S.HelpText>

                    <S.FormField>
                      <S.FormLabel>Entry</S.FormLabel>
                      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        <span style={{ flex: '1 1 auto' }} data-testid="dev-gamma-ray-entry">
                          {gammaRayEntryLabel(gammaRayEntry)}
                        </span>
                        <S.SecondaryButton
                          type="button"
                          onClick={createGammaRayEntry}
                          disabled={isLoading}
                          data-testid="dev-gamma-ray-create-entry"
                          title="Seeds an applicant with a paystub and a bank statement and assigns the review to you. Uses the property picked above, or the first available one."
                        >
                          {isLoading ? 'Working…' : 'Create new entry'}
                        </S.SecondaryButton>
                      </div>
                    </S.FormField>

                    {gammaRayEntry && (
                      <S.FormField>
                        <S.FormLabel>
                          Documents to evaluate ({gammaRayProofIds.length} of{' '}
                          {gammaRayEntry.proofs.length})
                        </S.FormLabel>
                        {gammaRayEntry.proofs.map((proof) => (
                          <S.DocumentItem key={proof.proof_id}>
                            <S.DocumentCheckbox
                              type="checkbox"
                              checked={gammaRayProofIds.includes(proof.proof_id)}
                              onChange={() => toggleGammaRayProof(proof.proof_id)}
                              data-testid={`dev-gamma-ray-proof-${proof.proof_id}`}
                            />
                            <span style={{ flex: '1 1 auto' }}>
                              {proof.type} · {proof.proof_id.slice(0, 8)}
                            </span>
                          </S.DocumentItem>
                        ))}
                        <S.HelpText>
                          One evaluation per checked document, each with its own set of check
                          results.
                        </S.HelpText>
                      </S.FormField>
                    )}

                    <S.FormField>
                      <S.FormLabel>Base scenario</S.FormLabel>
                      <S.FormSelect
                        value={gammaRayScenarioId}
                        onChange={(e) => applyGammaRayScenario(e.target.value)}
                        data-testid="dev-gamma-ray-scenario"
                      >
                        {gammaRayCatalog?.scenarios.map((scenario) => (
                          <option key={scenario.id} value={scenario.id}>
                            {scenario.label}
                          </option>
                        ))}
                      </S.FormSelect>
                      <S.HelpText>Fills every answer below. Change any of them after.</S.HelpText>
                    </S.FormField>

                    {(gammaRayCatalog?.fraud_trend_checks ?? []).length > 0 && (
                      <S.FormLabel>Fraud trend checks</S.FormLabel>
                    )}
                    {(gammaRayCatalog?.fraud_trend_checks ?? []).map((check) => (
                      <S.FormField key={check.engine_key} style={{ marginBottom: '0.75rem' }}>
                        <div
                          style={{
                            display: 'flex',
                            gap: '1rem',
                            alignItems: 'center',
                            flexWrap: 'wrap'
                          }}
                        >
                          <S.FormLabel style={{ flex: '1 1 14rem', marginBottom: 0 }}>
                            {check.display_name}
                          </S.FormLabel>
                          <S.FormSelect
                            style={{ flex: '0 1 12rem' }}
                            value={gammaRayTrendTemplate(check)}
                            onChange={(e) =>
                              setGammaRayTrend(
                                check,
                                e.target.value,
                                gammaRayTrendValue(check.engine_key)
                              )
                            }
                            aria-label={`${check.display_name} template`}
                            data-testid={`dev-gamma-ray-trend-template-${check.engine_key}`}
                          >
                            {check.templates.map((template) => (
                              <option key={template} value={template}>
                                {template}
                              </option>
                            ))}
                          </S.FormSelect>
                          <S.FormSelect
                            style={{ flex: '1 1 14rem' }}
                            value={gammaRayTrendValue(check.engine_key)}
                            onChange={(e) =>
                              setGammaRayTrend(check, gammaRayTrendTemplate(check), e.target.value)
                            }
                            aria-label={`${check.display_name} answer`}
                            data-testid={`dev-gamma-ray-trend-${check.engine_key}`}
                          >
                            {(gammaRayCatalog?.trend_variants ?? []).map((variant) => (
                              <option key={variant} value={variant}>
                                {GAMMA_RAY_VARIANT_LABELS[variant] ?? variant}
                              </option>
                            ))}
                            {gammaRayCatalog?.not_run_reasons.map((reason) => (
                              <option key={reason} value={`${GAMMA_RAY_NOT_RUN}${reason}`}>
                                Not run, {reason}
                              </option>
                            ))}
                          </S.FormSelect>
                        </div>
                        <S.HelpText>Requested for every PDF except offer letters.</S.HelpText>
                      </S.FormField>
                    ))}

                    <div
                      style={{
                        display: 'flex',
                        gap: '1rem',
                        alignItems: 'center',
                        marginTop: '1rem'
                      }}
                    >
                      <S.FormLabel style={{ flex: '1 1 auto', marginBottom: 0 }}>
                        List checks
                      </S.FormLabel>
                      <S.DocumentItem style={{ marginBottom: 0 }}>
                        <S.DocumentCheckbox
                          type="checkbox"
                          checked={gammaRayShowScores}
                          onChange={(e) => setGammaRayShowScores(e.target.checked)}
                          data-testid="dev-gamma-ray-show-scores"
                        />
                        <span>Show candidate scores</span>
                      </S.DocumentItem>
                    </div>
                    <S.HelpText>
                      A candidate is a row on the check&apos;s list that might be this document,
                      scored 0 to 1 by how close it is. The real engine returns up to five real
                      rows; here you choose how many, and the simulator makes up their names and
                      fields (&quot;Candidate 1&quot;, &quot;Row 3&quot;). When the result is Not
                      certain or Pick one, the review lists them and the analyst selects the one
                      that matches, or None of these. Cleared and Match resolve on their own.
                    </S.HelpText>

                    {gammaRayCatalog?.checks.map((check) => {
                      const row = gammaRayRows.find((r) => r.engine_key === check.engine_key);
                      if (!row) return null;

                      const isNotRun = row.not_run_reason !== null;
                      const band = gammaRayBand(check, row);
                      const atCap = row.candidates.length >= gammaRayCatalog.max_candidates;

                      return (
                        <S.FormField key={check.engine_key} style={{ marginBottom: '0.75rem' }}>
                          <div
                            style={{
                              display: 'flex',
                              gap: '1rem',
                              alignItems: 'center',
                              flexWrap: 'wrap'
                            }}
                          >
                            <S.FormLabel style={{ flex: '1 1 14rem', marginBottom: 0 }}>
                              {check.display_name}
                            </S.FormLabel>

                            <S.DocumentItem style={{ marginBottom: 0 }}>
                              <S.DocumentCheckbox
                                type="checkbox"
                                checked={isNotRun}
                                onChange={(e) =>
                                  toggleGammaRayNotRun(check.engine_key, e.target.checked)
                                }
                                data-testid={`dev-gamma-ray-notrun-${check.engine_key}`}
                              />
                              <span>Not run</span>
                            </S.DocumentItem>

                            {isNotRun ? (
                              <S.FormSelect
                                style={{ flex: '1 1 10rem' }}
                                value={row.not_run_reason ?? ''}
                                onChange={(e) =>
                                  updateGammaRayRow(check.engine_key, {
                                    not_run_reason: e.target.value
                                  })
                                }
                                data-testid={`dev-gamma-ray-reason-${check.engine_key}`}
                              >
                                {gammaRayCatalog.not_run_reasons.map((reason) => (
                                  <option key={reason} value={reason}>
                                    {reason}
                                  </option>
                                ))}
                              </S.FormSelect>
                            ) : (
                              <S.FormSelect
                                style={{ flex: '1 1 14rem' }}
                                value={gammaRayResultOf(band)}
                                onChange={(e) =>
                                  setGammaRayResult(check, e.target.value as GammaRayResult)
                                }
                                aria-label={`${check.display_name} answer`}
                                data-testid={`dev-gamma-ray-result-${check.engine_key}`}
                              >
                                {GAMMA_RAY_RESULTS.map((result) => (
                                  <option key={result.value} value={result.value}>
                                    {result.label}
                                  </option>
                                ))}
                              </S.FormSelect>
                            )}

                            {!isNotRun && gammaRayShowScores && (
                              <S.SecondaryButton
                                type="button"
                                onClick={() => addGammaRayCandidate(check.engine_key)}
                                disabled={atCap}
                                data-testid={`dev-gamma-ray-add-${check.engine_key}`}
                                title={`Up to ${gammaRayCatalog.max_candidates} candidates, best score first on the wire`}
                              >
                                Add candidate
                              </S.SecondaryButton>
                            )}
                          </div>

                          {!isNotRun &&
                            gammaRayShowScores &&
                            row.candidates.map((candidate, index) => (
                              <div
                                key={index}
                                style={{
                                  display: 'flex',
                                  gap: '0.5rem',
                                  alignItems: 'center',
                                  marginTop: '0.25rem'
                                }}
                              >
                                <S.FormInput
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max="1"
                                  aria-label={`${check.display_name} candidate ${index + 1} score`}
                                  style={{ flex: '0 1 7rem' }}
                                  value={candidate.score}
                                  onChange={(e) =>
                                    updateGammaRayCandidate(check.engine_key, index, e.target.value)
                                  }
                                  data-testid={`dev-gamma-ray-score-${check.engine_key}-${index}`}
                                />
                                <S.SecondaryButton
                                  type="button"
                                  onClick={() => removeGammaRayCandidate(check.engine_key, index)}
                                  data-testid={`dev-gamma-ray-remove-${check.engine_key}-${index}`}
                                >
                                  Remove
                                </S.SecondaryButton>
                                <S.HelpText
                                  style={{ margin: 0 }}
                                  data-testid={`dev-gamma-ray-candidate-${check.engine_key}-${index}`}
                                >
                                  {gammaRayCandidateLabel(check, row, index)}
                                </S.HelpText>
                              </div>
                            ))}

                          {band && gammaRayShowScores && (
                            <S.HelpText data-testid={`dev-gamma-ray-band-${check.engine_key}`}>
                              Reads as {band} against the {check.match_bar} bar and {check.floor}{' '}
                              floor.
                            </S.HelpText>
                          )}
                        </S.FormField>
                      );
                    })}

                    <S.SubmitButton
                      type="button"
                      onClick={publishGammaRayResults}
                      disabled={
                        isLoading ||
                        !gammaRayEntry ||
                        gammaRayProofIds.length === 0 ||
                        gammaRayRows.length === 0 ||
                        !gammaRayScoresComplete(gammaRayRows)
                      }
                      style={{ marginTop: '1rem' }}
                      data-testid="dev-gamma-ray-publish"
                    >
                      {isLoading ? 'Working…' : 'Request evaluation'}
                    </S.SubmitButton>
                  </>
                )}
              </S.FormSection>

              <S.FormSection style={{ marginTop: '1.5rem' }}>
                <S.SectionTitle>VOA Screenshot Staging (real FVS data)</S.SectionTitle>
                <S.HelpText style={{ marginBottom: '1.5rem' }}>
                  Stages a review for an applicant FVS already has a record for, so the review
                  screen renders real data and real document PDFs in deployed dev (where the
                  simulated client is not wired). The review opens read-only-safe: browse and
                  screenshot freely, but do not Submit — that posts a real completion to FVS.
                </S.HelpText>

                <div
                  style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}
                >
                  <S.SubmitButton
                    type="button"
                    onClick={loadVoaScreenshotCandidates}
                    disabled={voaCandidatesLoading}
                    data-testid="dev-voa-load-candidates"
                    title={
                      'Lists applicants with completed VOA reviews, classified from live FVS ' +
                      'data as simple, multi-account, or multi-document.'
                    }
                  >
                    {voaCandidatesLoading
                      ? 'Loading…'
                      : voaCandidates.length > 0
                      ? 'Refresh candidates ⓘ'
                      : 'Load candidates ⓘ'}
                  </S.SubmitButton>
                </div>

                {voaCandidates.length > 0 && (
                  <>
                    <S.FormField>
                      <S.FormLabel>Candidate</S.FormLabel>
                      <S.FormSelect
                        value={selectedVoaCandidate}
                        onChange={(e) => setSelectedVoaCandidate(e.target.value)}
                        data-testid="dev-voa-candidate"
                      >
                        {voaCandidates.map((candidate) => (
                          <option key={candidate.applicant_id} value={candidate.applicant_id}>
                            {voaCandidateLabel(candidate)}
                          </option>
                        ))}
                      </S.FormSelect>
                    </S.FormField>

                    <S.SubmitButton
                      type="button"
                      onClick={stageVoaScreenshotReview}
                      disabled={isLoading || !selectedVoaCandidate}
                      data-testid="dev-voa-stage-screenshot"
                      title={
                        'Assigns the review to you and opens the review screen. Any active ' +
                        'review item for the applicant is archived first.'
                      }
                    >
                      {isLoading ? 'Working…' : 'Stage & open review ⓘ'}
                    </S.SubmitButton>
                  </>
                )}
              </S.FormSection>

              <S.FormSection style={{ marginTop: '1.5rem' }}>
                <S.SectionTitle>Review Scenario Configuration</S.SectionTitle>

                <form onSubmit={handleSubmit}>
                  <S.FormField>
                    <S.FormLabel htmlFor="property_short_id">Property Short ID</S.FormLabel>
                    <S.FormInput
                      id="property_short_id"
                      type="text"
                      value={formData.property_short_id}
                      onChange={(e) => handleInputChange('property_short_id', e.target.value)}
                      placeholder="e.g., A1b2C3d4E5"
                      required
                    />

                    {propertyData && propertyData.length > 0 && (
                      <S.ModernSearchableDropdown ref={dropdownRef}>
                        <S.ModernDropdownButton
                          isOpen={isDropdownOpen}
                          onClick={handleDropdownToggle}
                        >
                          <S.ModernDropdownInput
                            type="text"
                            placeholder={`Search ${propertyData?.length || 0} properties...`}
                            value={propertySearchTerm}
                            onChange={(e) => {
                              handlePropertySearch(e.target.value);
                              if (!isDropdownOpen) setIsDropdownOpen(true);
                            }}
                            onKeyDown={handleKeyDown}
                            onFocus={() => setIsDropdownOpen(true)}
                            onClick={(e) => e.stopPropagation()}
                            autoComplete="off"
                          />
                          <S.ModernDropdownArrow isOpen={isDropdownOpen} />
                        </S.ModernDropdownButton>

                        {isDropdownOpen && (
                          <S.ModernDropdownMenu onClick={(e) => e.stopPropagation()}>
                            {filteredProperties.length > 0 ? (
                              filteredProperties.map((property, index) => (
                                <S.ModernDropdownOption
                                  key={property.id}
                                  isHighlighted={index === highlightedIndex}
                                  isSelected={formData.property_short_id === property.short_id}
                                  onClick={() => handlePropertySelect(property)}
                                >
                                  <S.ModernOptionContent>
                                    <S.ModernOptionId>
                                      {property.name}
                                      {property.property_features?.some(
                                        (f: { code?: string; state?: string }) =>
                                          f.code === 'identity_verification' &&
                                          f.state === 'enabled'
                                      ) && (
                                        <S.IVEnabledIndicator>(IV Enabled)</S.IVEnabledIndicator>
                                      )}
                                    </S.ModernOptionId>
                                    <S.ModernOptionName>{property.short_id}</S.ModernOptionName>
                                  </S.ModernOptionContent>
                                </S.ModernDropdownOption>
                              ))
                            ) : (
                              <S.ModernDropdownEmpty>
                                No properties found matching &quot;{propertySearchTerm}&quot;
                              </S.ModernDropdownEmpty>
                            )}
                          </S.ModernDropdownMenu>
                        )}
                      </S.ModernSearchableDropdown>
                    )}
                    <S.HelpText>
                      {isLoadingProperties
                        ? 'Loading properties...'
                        : propertyData && propertyData.length > 0
                        ? 'Use the searchable dropdown above to find and select a property, or enter a property short ID manually'
                        : 'Enter a valid property short ID manually'}
                    </S.HelpText>
                  </S.FormField>

                  <S.ManualDocumentsSection>
                    <S.FormLabel>Manual Documents</S.FormLabel>
                    {formData.manual_documents.map((doc, index) => (
                      <S.DocumentItem key={index}>
                        <S.DocumentSelect
                          value={doc.type}
                          onChange={(e) =>
                            handleManualDocumentChange(index, 'type', e.target.value)
                          }
                        >
                          {documentTypes.map((type) => (
                            <option key={type} value={type}>
                              {type.split('_').join(' ').toUpperCase()}
                            </option>
                          ))}
                        </S.DocumentSelect>

                        <label
                          htmlFor={`ruled_by_av_${index}`}
                          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        >
                          <S.DocumentCheckbox
                            id={`ruled_by_av_${index}`}
                            type="checkbox"
                            checked={doc.ruled_by_av}
                            onChange={(e) =>
                              handleManualDocumentChange(index, 'ruled_by_av', e.target.checked)
                            }
                          />
                          Ruled by AV
                        </label>

                        {doc.ruled_by_av && (
                          <S.DocumentSelect
                            value={doc.av_result || ''}
                            onChange={(e) =>
                              handleManualDocumentChange(index, 'av_result', e.target.value)
                            }
                          >
                            <option value="">Select AV Result...</option>
                            {avResults.map((result) => (
                              <option key={result} value={result}>
                                {result}
                              </option>
                            ))}
                          </S.DocumentSelect>
                        )}

                        {formData.manual_documents.length > 1 && (
                          <S.RemoveButton type="button" onClick={() => removeManualDocument(index)}>
                            Remove
                          </S.RemoveButton>
                        )}
                      </S.DocumentItem>
                    ))}

                    <S.AddButton type="button" onClick={addManualDocument}>
                      Add Manual Document
                    </S.AddButton>
                  </S.ManualDocumentsSection>

                  <S.FormField>
                    <S.FormLabel htmlFor="potential_matches">Potential Matches</S.FormLabel>
                    <S.FormInput
                      id="potential_matches"
                      type="number"
                      min="0"
                      value={formData.potential_matches}
                      onChange={(e) =>
                        handleInputChange('potential_matches', parseInt(e.target.value) || 0)
                      }
                    />
                  </S.FormField>

                  <S.FormField>
                    <S.FormLabel htmlFor="reviewer_id">Reviewer ID (Optional)</S.FormLabel>
                    <S.FormInput
                      id="reviewer_id"
                      type="text"
                      value={formData.reviewer_id}
                      onChange={(e) => handleInputChange('reviewer_id', e.target.value)}
                      placeholder="Leave empty to use current user"
                    />
                    <S.HelpText>Leave empty to add to queue</S.HelpText>
                  </S.FormField>

                  <S.FormField>
                    <label
                      htmlFor="redirect_toggle"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        cursor: 'pointer'
                      }}
                    >
                      <S.DocumentCheckbox
                        id="redirect_toggle"
                        type="checkbox"
                        checked={redirectToReview}
                        onChange={(e) => setRedirectToReview(e.target.checked)}
                      />
                      <S.FormLabel style={{ margin: 0, cursor: 'pointer' }}>
                        Go to review after creating scenario
                      </S.FormLabel>
                    </label>
                    <S.HelpText>
                      When enabled, you&apos;ll be automatically redirected to the created review
                    </S.HelpText>
                  </S.FormField>

                  <S.ActionButtonsGrid>
                    <S.SubmitButton type="submit" disabled={isLoading}>
                      {isLoading ? 'Creating...' : 'Create Review Scenario'}
                    </S.SubmitButton>
                  </S.ActionButtonsGrid>
                </form>
              </S.FormSection>

              <S.UsageNotesSection>
                <S.UsageTitle>Usage Notes</S.UsageTitle>
                <S.UsageList>
                  <li>This tab creates review scenarios for fraud detection workflows</li>
                  <li>Manual documents can be configured with different types and AV results</li>
                  <li>
                    <strong>At least one document must NOT be ruled by AV</strong> for manual review
                  </li>
                  <li>You can have additional AV-ruled documents alongside non-AV documents</li>
                  <li>
                    Potential matches create duplicate applicant entries for testing fraud detection
                  </li>
                </S.UsageList>
              </S.UsageNotesSection>
            </>
          )}

          {/* Signal Scenarios Tab */}
          {activeTab === 'signals' && (
            <>
              <S.FormSection>
                <S.SectionTitle>Signal Scenarios</S.SectionTitle>
                <S.HelpText style={{ marginBottom: '1.5rem' }}>
                  Create Application Pattern Signal scenarios. These create the &quot;N-1&quot;
                  pre-trigger state - the next submission with the same applicant identity will
                  trigger the signal.
                </S.HelpText>

                <S.FormField>
                  <S.FormLabel>Signal Condition</S.FormLabel>
                  <S.FormSelect
                    value={signalCondition}
                    onChange={(e) => setSignalCondition(e.target.value as SignalCondition)}
                  >
                    <option value="high_volume">
                      High Volume (6 entries across 6 properties, 7th triggers)
                    </option>
                    <option value="patterned_fraud">
                      Patterned Fraud (2 entries in 2 cities with EDITED, 3rd city triggers)
                    </option>
                  </S.FormSelect>
                </S.FormField>

                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                  <S.SubmitButton
                    onClick={handleSignalSubmit}
                    disabled={signalLoading || cleanupLoading}
                  >
                    {signalLoading ? 'Creating...' : 'Create Signal Scenario'}
                  </S.SubmitButton>
                  <S.SubmitButton
                    onClick={handleSignalCleanup}
                    disabled={signalLoading || cleanupLoading}
                    style={{ backgroundColor: theme.color.red[500] }}
                  >
                    {cleanupLoading ? 'Cleaning up...' : 'Clean Up Previous Scenarios'}
                  </S.SubmitButton>
                </div>

                {signalResult && (
                  <S.SignalResultCard>
                    <S.ResultHeader>Scenario Created Successfully</S.ResultHeader>

                    <S.ResultSection>
                      <S.ResultLabel>
                        Applicant Identity (use for triggering submission):
                      </S.ResultLabel>
                      <S.ResultCode>
                        Email: {signalResult.applicant_identity.email}
                        <br />
                        Phone: {signalResult.applicant_identity.phone}
                        <br />
                        Name: {signalResult.applicant_identity.first_name}{' '}
                        {signalResult.applicant_identity.last_name}
                      </S.ResultCode>
                    </S.ResultSection>

                    <S.ResultSection>
                      <S.ResultLabel>Trigger Property Applicant Link:</S.ResultLabel>
                      <S.HelpText style={{ marginBottom: '8px' }}>
                        Click the link below to open the applicant submission form for the trigger
                        property ({signalResult.trigger_property.city || 'N/A'})
                      </S.HelpText>
                      <S.ApplicantLinkContainer>
                        <S.ApplicantLink
                          href={getTriggerApplicantLink()}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open Applicant Form
                        </S.ApplicantLink>
                        <S.CopyLinkButton onClick={() => handleCopyLink(getTriggerApplicantLink())}>
                          {linkCopied ? '✓ Copied!' : 'Copy Link'}
                        </S.CopyLinkButton>
                      </S.ApplicantLinkContainer>
                    </S.ResultSection>

                    <S.ResultSection>
                      <S.ResultLabel>
                        Created Entries ({signalResult.entries.length}):
                      </S.ResultLabel>
                      <S.ResultList>
                        {signalResult.entries.map((entry) => (
                          <li key={entry.entry_id}>
                            {entry.folder_name} (Entry: {entry.entry_id.slice(0, 8)}...)
                          </li>
                        ))}
                      </S.ResultList>
                    </S.ResultSection>

                    {signalResult.cities && (
                      <S.ResultSection>
                        <S.ResultLabel>Cities Used:</S.ResultLabel>
                        <S.ResultCode>{signalResult.cities.join(', ')}</S.ResultCode>
                      </S.ResultSection>
                    )}
                  </S.SignalResultCard>
                )}
              </S.FormSection>

              <S.UsageNotesSection>
                <S.UsageTitle>Usage Notes</S.UsageTitle>
                <S.UsageList>
                  <li>
                    Signal scenarios create the &quot;N-1&quot; pre-trigger state for Application
                    Pattern Signals
                  </li>
                  <li>
                    <strong>High Volume:</strong> Creates 6 entries across 6 properties. The 7th
                    submission triggers the signal.
                  </li>
                  <li>
                    <strong>Patterned Fraud:</strong> Creates 2 entries in 2 different cities with
                    EDITED results. The 3rd city triggers the signal.
                  </li>
                  <li>
                    Use the provided applicant identity (email, phone, name) when filling out the
                    trigger submission
                  </li>
                  <li>
                    Click the applicant link to open the submission form for the trigger property
                  </li>
                  <li>
                    <strong>Clean Up:</strong> Use the &quot;Clean Up Previous Scenarios&quot;
                    button to delete all scenario data associated with your email before re-running
                    a demo
                  </li>
                </S.UsageList>
              </S.UsageNotesSection>
            </>
          )}
        </S.TabContent>
      </div>
    </ListView>
  );
};

export default DevDashboardPage;
